import { useEffect, useState } from "react";
import Select from "react-select";
import moment from "moment";
import { PrivateAxios } from "../../environment/AxiosInstance";
import { toast } from "react-toastify";
import { UserAuth } from '../auth/Auth';
import { CurrencyList } from '../../utils/StaticData';
import './AddTarget.css';

const MAX_AMOUNT = 1e12;
const MAX_NUMBER = 1e8;
const MIN_REASONABLE_AMOUNT = 1000;

export default function AddTarget() {
    const { User, MatchPermission } = UserAuth();

    const [assignees, setAssignees] = useState([]);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [period, setPeriod] = useState("daily");
    // Dates as YYYY-MM-DD strings so the native <input type="date"> can
    // bind directly without any popper/positioning library.
    const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [targetAmount, setTargetAmount] = useState("");
    const [currency, setCurrency] = useState("INR");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDarkTheme, setIsDarkTheme] = useState(false);
    const [targetType, setTargetType] = useState("amount");
    const [targetNumber, setTargetNumber] = useState("");
    const [errors, setErrors] = useState({});

    // Per-assignee map: user id -> { amount, number }
    const [useIndividualTargets, setUseIndividualTargets] = useState(false);
    const [individualTargets, setIndividualTargets] = useState({});

    useEffect(() => {
        const checkTheme = () => setIsDarkTheme(document.body.classList.contains("dark-theme"));
        checkTheme();
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!startDate) return;
        const s = moment(startDate);
        if (!s.isValid()) return;
        let end;
        switch (period) {
            case "weekly": end = s.clone().add(6, "days"); break;
            case "monthly": end = s.clone().endOf("month"); break;
            case "quarterly": end = s.clone().add(3, "months").endOf("month"); break;
            case "yearly": end = s.clone().endOf("year"); break;
            default: end = s.clone();
        }
        setEndDate(end.format('YYYY-MM-DD'));
    }, [startDate, period]);

    useEffect(() => {
        setIndividualTargets((prev) => {
            const next = {};
            assignees.forEach((a) => {
                next[a.id] = prev[a.id] || { amount: '', number: '' };
            });
            return next;
        });
    }, [assignees]);

    if (!MatchPermission(['Sales All', 'Add Sales'])) {
        return (
            <div className="p-4 text-center">
                <h4>Access Denied</h4>
                <p>You don't have permission to add sales targets.</p>
            </div>
        );
    }

    const individualTotal = () => {
        return assignees.reduce((sum, a) => {
            const v = individualTargets[a.id] || {};
            const raw = targetType === 'amount' ? v.amount : v.number;
            const n = Number(raw);
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
    };

    function validateForm() {
        const next = {};
        const trimmedTitle = title.trim();
        if (!trimmedTitle) next.title = "Required";
        else if (trimmedTitle.length > 200) next.title = "Max 200 characters";

        if (assignees.length === 0) next.assignees = "Select at least one assignee";

        if (!startDate) next.startDate = "Required";
        if (!endDate) next.endDate = "Required";
        else if (startDate && endDate && moment(endDate).isBefore(moment(startDate), "day")) {
            next.endDate = "End date must be on or after start date";
        }

        if (!CurrencyList.find(c => c.code === currency)) {
            next.currency = "Unsupported currency";
        }

        if (useIndividualTargets) {
            const perErrors = {};
            assignees.forEach((a) => {
                const v = individualTargets[a.id] || {};
                const raw = targetType === 'amount' ? v.amount : v.number;
                const n = Number(raw);
                if (raw === '' || raw === undefined || raw === null) {
                    perErrors[a.id] = "Required";
                } else if (!Number.isFinite(n) || n <= 0) {
                    perErrors[a.id] = "Must be > 0";
                } else if (targetType === 'amount' && n > MAX_AMOUNT) {
                    perErrors[a.id] = "Too large";
                } else if (targetType === 'number') {
                    if (!Number.isInteger(n)) perErrors[a.id] = "Whole number";
                    else if (n > MAX_NUMBER) perErrors[a.id] = "Too large";
                }
            });
            if (Object.keys(perErrors).length > 0) {
                next.individualTargets = perErrors;
            }
        } else if (targetType === "amount") {
            const n = Number(targetAmount);
            if (!targetAmount && targetAmount !== 0) next.targetAmount = "Required";
            else if (!Number.isFinite(n) || n <= 0) next.targetAmount = "Must be > 0";
            else if (n > MAX_AMOUNT) next.targetAmount = "Value too large";
        } else {
            const n = Number(targetNumber);
            if (!targetNumber && targetNumber !== 0) next.targetNumber = "Required";
            else if (!Number.isFinite(n) || n <= 0) next.targetNumber = "Must be > 0";
            else if (!Number.isInteger(n)) next.targetNumber = "Must be a whole number";
            else if (n > MAX_NUMBER) next.targetNumber = "Value too large";
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!MatchPermission(['Sales All', 'Add Sales'])) {
            toast.error("You don't have permission to create sales targets");
            return;
        }
        if (!validateForm()) {
            toast.warning("Please fix the highlighted fields");
            return;
        }

        let payloadTargetAmount = 0;
        let payloadTargetNumber = 0;
        let assigneeTargets = null;

        if (useIndividualTargets) {
            assigneeTargets = assignees.map((a) => {
                const v = individualTargets[a.id] || {};
                return {
                    assignee_id: a.id,
                    assigned_amount: targetType === 'amount' ? Number(v.amount) : 0,
                    assigned_number: targetType === 'number' ? Number(v.number) : 0,
                };
            });
            const total = individualTotal();
            if (targetType === 'amount') payloadTargetAmount = total;
            else payloadTargetNumber = total;
        } else {
            payloadTargetAmount = targetType === 'amount' ? Number(targetAmount) : 0;
            payloadTargetNumber = targetType === 'number' ? Number(targetNumber) : 0;
        }

        if (targetType === "amount" && payloadTargetAmount < MIN_REASONABLE_AMOUNT) {
            const sym = (CurrencyList.find(c => c.code === currency) || {}).symbol || '';
            const ok = window.confirm(
                "Target amount of " + sym + Number(payloadTargetAmount).toLocaleString('en-IN') + " " + currency + " looks unusually small. Save anyway?"
            );
            if (!ok) return;
        }

        setIsSubmitting(true);
        try {
            const body = {
                title: title.trim(),
                endDate: moment(endDate).format("YYYY-MM-DD"),
                assignees: assignees.map((a) => a.id),
                description,
                startDate: moment(startDate).format("YYYY-MM-DD"),
                period,
                targetAmount: payloadTargetAmount,
                targetNumber: payloadTargetNumber,
                targetType,
                currency,
            };
            if (assigneeTargets) body.assigneeTargets = assigneeTargets;
            await PrivateAxios.post("/sales-targets", body);
            toast.success("Sales target created successfully");
            resetForm();
        } catch (err) {
            const msg = (err && err.response && err.response.data && err.response.data.message) || "Failed to create sales target";
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setTitle("");
        setDescription("");
        setTargetAmount("");
        setTargetNumber("");
        setTargetType("amount");
        setCurrency("INR");
        setAssignees([]);
        setStartDate(moment().format('YYYY-MM-DD'));
        setPeriod("daily");
        setErrors({});
        setUseIndividualTargets(false);
        setIndividualTargets({});
    };

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderRadius: '6px',
            borderColor: state.isFocused ? '#5034ff' : '#d6d9e6',
            boxShadow: state.isFocused ? '0 0 0 4px rgba(80,52,255,0.06)' : 'none',
            minHeight: '44px',
            '&:hover': { borderColor: '#5034ff' }
        }),
        multiValue: (base) => ({ ...base, backgroundColor: '#e8e4ff', borderRadius: '4px' }),
        multiValueLabel: (base) => ({ ...base, color: '#5034ff', fontWeight: '500' }),
        multiValueRemove: (base) => ({ ...base, color: '#5034ff', ':hover': { backgroundColor: '#5034ff', color: 'white' } })
    };

    function FieldError({ name }) {
        if (!errors[name]) return null;
        return <div className="invalid-feedback d-block small">{errors[name]}</div>;
    }

    const currencySymbol = (CurrencyList.find(c => c.code === currency) || {}).symbol || 'Rs';
    const indivErrs = (errors.individualTargets || {});

    return (
        <div className="add-target-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="add-target-title mb-0">
                    <i className="fas fa-bullseye me-2"></i>Add Sales Target
                </h4>
            </div>

            <form onSubmit={handleSubmit} className="card add-target-card" noValidate>
                <div className="row">
                    <div className="col-md-12 mb-3">
                        <label className="form-label">
                            Target Title <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className={"form-control add-target-input " + (errors.title ? 'is-invalid' : '')}
                            placeholder="Enter target title"
                            value={title}
                            maxLength={200}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                        <FieldError name="title" />
                    </div>

                    <div className="col-md-12 mb-3">
                        <label className="form-label">
                            Assign To <span className="text-danger">*</span>
                        </label>
                        <div className="react-select-wrap">
                            <Select
                                options={User}
                                isMulti
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                                value={assignees}
                                onChange={setAssignees}
                                placeholder="Select assignees..."
                                classNamePrefix="rt"
                                styles={customSelectStyles}
                                noOptionsMessage={() => "No users available"}
                            />
                        </div>
                        <FieldError name="assignees" />
                        {assignees.length > 0 && (
                            <small className="text-muted">
                                {assignees.length} assignee{assignees.length > 1 ? 's' : ''} selected
                            </small>
                        )}
                    </div>

                    <div className="col-md-12 mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Enter target description (optional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">
                            Start Date <span className="text-danger">*</span>
                        </label>
                        <input
                            type="date"
                            className={"form-control add-target-input " + (errors.startDate ? 'is-invalid' : '')}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                        <FieldError name="startDate" />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">
                            Target Period <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select add-target-input"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="yearly">Yearly</option>
                        </select>
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">
                            End Date <span className="text-danger">*</span>
                        </label>
                        <input
                            type="date"
                            className={"form-control add-target-input " + (errors.endDate ? 'is-invalid' : '')}
                            value={endDate}
                            min={startDate || undefined}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                        <FieldError name="endDate" />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">
                            Currency <span className="text-danger">*</span>
                        </label>
                        <select
                            className={"form-control add-target-input " + (errors.currency ? 'is-invalid' : '')}
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                        >
                            {CurrencyList.map((curr) => (
                                <option key={curr.code} value={curr.code}>
                                    {curr.symbol} - {curr.name} ({curr.code})
                                </option>
                            ))}
                        </select>
                        <FieldError name="currency" />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">
                            Target Type <span className="text-danger">*</span>
                        </label>
                        <select
                            className="form-select add-target-input"
                            value={targetType}
                            onChange={(e) => setTargetType(e.target.value)}
                        >
                            <option value="amount">Amount Target</option>
                            <option value="number">Number Target</option>
                        </select>
                    </div>

                    <div className="col-md-12 mb-3">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="useIndividualTargetsSwitch"
                                checked={useIndividualTargets}
                                onChange={(e) => setUseIndividualTargets(e.target.checked)}
                                disabled={assignees.length === 0}
                            />
                            <label
                                className="form-check-label"
                                htmlFor="useIndividualTargetsSwitch"
                                title={assignees.length === 0 ? 'Select at least one assignee first' : ''}
                            >
                                <i className="fas fa-users-cog me-2"></i>
                                Assign individual target per user
                            </label>
                        </div>
                        <small className="text-muted">
                            When enabled, each assignee can have a different target value. The total is the sum of all per-user values.
                        </small>
                    </div>

                    {!useIndividualTargets && targetType === "amount" && (
                        <div className="col-md-6 mb-3">
                            <label className="form-label">
                                Target Amount <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                                <span className="input-group-text">{currencySymbol}</span>
                                <input
                                    type="number"
                                    className={"form-control add-target-input " + (errors.targetAmount ? 'is-invalid' : '')}
                                    placeholder="Enter target amount"
                                    value={targetAmount}
                                    onChange={(e) => setTargetAmount(e.target.value)}
                                    min="0"
                                    max={MAX_AMOUNT}
                                    step="0.01"
                                />
                            </div>
                            <FieldError name="targetAmount" />
                        </div>
                    )}

                    {!useIndividualTargets && targetType === "number" && (
                        <div className="col-md-6 mb-3">
                            <label className="form-label">
                                Target Number <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className={"form-control add-target-input " + (errors.targetNumber ? 'is-invalid' : '')}
                                placeholder="Enter target number"
                                value={targetNumber}
                                onChange={(e) => setTargetNumber(e.target.value)}
                                min="0"
                                max={MAX_NUMBER}
                                step="1"
                            />
                            <FieldError name="targetNumber" />
                        </div>
                    )}

                    {useIndividualTargets && assignees.length > 0 && (
                        <div className="col-md-12 mb-3">
                            <label className="form-label">
                                Per-Assignee {targetType === 'amount' ? 'Amount' : 'Number'} <span className="text-danger">*</span>
                            </label>
                            <div className="individual-targets-table table-responsive">
                                <table className="table table-bordered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '40%' }}>Assignee</th>
                                            <th>{targetType === 'amount' ? ('Target Amount (' + currencySymbol + ')') : 'Target Number'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignees.map((a) => {
                                            const v = individualTargets[a.id] || {};
                                            const fieldVal = targetType === 'amount' ? (v.amount || '') : (v.number || '');
                                            const err = indivErrs[a.id];
                                            const setField = (key, val) => {
                                                setIndividualTargets((prev) => ({
                                                    ...prev,
                                                    [a.id]: { ...(prev[a.id] || {}), [key]: val }
                                                }));
                                            };
                                            return (
                                                <tr key={a.id}>
                                                    <td>
                                                        <strong>{a.name}</strong>
                                                        {a.email && <div className="small text-muted">{a.email}</div>}
                                                    </td>
                                                    <td>
                                                        <div className="input-group">
                                                            {targetType === 'amount' && (
                                                                <span className="input-group-text">{currencySymbol}</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                className={"form-control add-target-input " + (err ? 'is-invalid' : '')}
                                                                placeholder={"Enter " + (targetType === 'amount' ? 'amount' : 'number')}
                                                                value={fieldVal}
                                                                min="0"
                                                                step={targetType === 'amount' ? '0.01' : '1'}
                                                                onChange={(e) => setField(targetType === 'amount' ? 'amount' : 'number', e.target.value)}
                                                            />
                                                        </div>
                                                        {err && <div className="invalid-feedback d-block small">{err}</div>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        <tr className="table-light">
                                            <td className="text-end"><strong>Total</strong></td>
                                            <td>
                                                <strong>
                                                    {targetType === 'amount'
                                                        ? (currencySymbol + new Intl.NumberFormat('en-IN').format(individualTotal()))
                                                        : new Intl.NumberFormat('en-IN').format(individualTotal())}
                                                </strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={resetForm}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-redo me-2"></i>Reset
                    </button>
                    <button
                        type="submit"
                        className="btn btn-exp-green add-target-btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Creating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-plus-circle me-2"></i>Create Target
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
